import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { TourCanvasElement, TourElement, TourStepProps } from '../tour.typings'
import TourCanvas from '../tourCanvas/tourCanvas'
import TourTexts from '../tourTexts/tourTexts'
import { useDispatch, useSelector } from 'react-redux'
import { selectCurrentStep, selectTourSteps } from '../../../selectors/tour'
import { closeTour, gotoNextStep } from '../../../reducer/tour.reducer'
import { findTourTarget } from '../resolveTourSteps'

/** How often a live step re-measures its targets: the page keeps moving underneath the tour. */
const REMEASURE_INTERVAL_MS = 250

const measure = (els: TourElement[]): TourCanvasElement[] => {
    const measured: TourCanvasElement[] = []
    els.forEach((el) => {
        const node = findTourTarget(el.selector)
        if (!node) return
        const bounds = node.getBoundingClientRect()
        measured.push({
            ...el,
            coordinates: {
                topLeft: { x: bounds.left, y: bounds.top },
                topRight: { x: bounds.right, y: bounds.top },
                bottomLeft: { x: bounds.left, y: bounds.bottom },
                bottomRight: { x: bounds.right, y: bounds.bottom }
            }
        })
    })
    return measured
}

const sameGeometry = (a: TourCanvasElement[], b: TourCanvasElement[]) =>
    a.length === b.length &&
    a.every((el, i) => {
        const other = b[i]
        return (
            el.selector === other.selector &&
            el.coordinates.topLeft.x === other.coordinates.topLeft.x &&
            el.coordinates.topLeft.y === other.coordinates.topLeft.y &&
            el.coordinates.bottomRight.x === other.coordinates.bottomRight.x &&
            el.coordinates.bottomRight.y === other.coordinates.bottomRight.y
        )
    })

const TourStep: React.FC<TourStepProps> = ({ elements }) => {
    const [canvasElements, setCanvasElements] = useState<TourCanvasElement[]>([])
    const previousElements = useRef<TourElement[]>([])
    const previousStyles = useRef<Record<string, Record<string, any>>>({})
    const steps = useSelector(selectTourSteps)
    const currentStepNumber = useSelector(selectCurrentStep)
    const dispatch = useDispatch()
    const stepRef = useRef({ currentStepNumber, stepsLength: steps.length })
    stepRef.current = { currentStepNumber, stepsLength: steps.length }
    const canvasElementsRef = useRef<TourCanvasElement[]>([])
    canvasElementsRef.current = canvasElements
    const pendingTimer = useRef<number | undefined>(undefined)

    /**
     * Measure the step's targets. A step whose targets have all gone (the page changed under
     * the tour) is skipped, or ends the tour when it is the last one.
     */
    const createCanvasElements = useCallback((els: TourElement[]) => {
        if (!els || !els.length) return
        const measured = measure(els)
        if (!measured.length) {
            const { currentStepNumber: step, stepsLength } = stepRef.current
            if (step < stepsLength - 1) {
                dispatch(gotoNextStep())
            } else {
                dispatch(closeTour())
            }
            return
        }
        if (!sameGeometry(measured, canvasElementsRef.current)) setCanvasElements(measured)
    }, [dispatch])

    const runExit = useCallback((te: TourElement) => {
        if (te.onExit) {
            const { selector, value } = te.onExit
            const el = document.querySelector<HTMLElement>(selector)
            if (!el) return
            if (te.onExit.type === 'click') {
                el.click()
            } else if (!te.onEnter) {
                for (const key in value) {
                    el.style.setProperty(key, value[key])
                }
            }
        }
        if (te.onEnter && te.onEnter.type === 'style') {
            const { selector, value } = te.onEnter
            const el = document.querySelector<HTMLElement>(selector)
            if (!el) return
            const prevStyle = previousStyles.current[selector] || {}
            for (const key in value) {
                if (prevStyle[key]) el.style.setProperty(key, prevStyle[key])
                else el.style.removeProperty(key)
            }
        }
    }, [])

    const runEnter = useCallback((te: TourElement) => {
        if (!te.onEnter) return
        const { selector, value } = te.onEnter
        const el = document.querySelector<HTMLElement>(selector)
        if (!el) return
        if (te.onEnter.type === 'click') {
            el.click()
            return
        }
        for (const key in value) {
            const currentValue = el.style.getPropertyValue(key)
            if (!previousStyles.current[selector]) previousStyles.current[selector] = {}
            if (currentValue) previousStyles.current[selector][key] = currentValue
            el.style.setProperty(key, value[key])
        }
    }, [])

    // Enter the step: undo the previous step's side effects, apply this step's, then measure
    // once its targets have had time to appear.
    useEffect(() => {
        if (!elements) return
        window.clearTimeout(pendingTimer.current)
        const key = (list: TourElement[]) => list.map((v) => v.selector).sort().join('\u0000')
        let delay = 0
        if (key(previousElements.current) !== key(elements)) {
            previousElements.current.forEach(runExit)
            previousStyles.current = {}
            elements.forEach((te) => {
                delay = Math.max(delay, te.delay || 0)
                runEnter(te)
            })
            previousElements.current = elements
        }
        if (delay) {
            setCanvasElements([])
            pendingTimer.current = window.setTimeout(() => createCanvasElements(elements), delay)
        } else {
            createCanvasElements(elements)
        }
        return () => window.clearTimeout(pendingTimer.current)
    }, [elements, createCanvasElements, runEnter, runExit])

    // Leaving the tour restores whatever the last step changed on the page.
    useEffect(
        () => () => {
            previousElements.current.forEach(runExit)
            previousElements.current = []
            previousStyles.current = {}
        },
        [runExit]
    )

    // Keep the spotlight on its targets while the page settles (the sidebar collapsing as the
    // tour starts, data arriving, the window resizing).
    useEffect(() => {
        if (!elements) return
        const interval = window.setInterval(() => {
            if (!canvasElementsRef.current.length) return
            const measured = measure(elements)
            if (measured.length && !sameGeometry(measured, canvasElementsRef.current)) setCanvasElements(measured)
        }, REMEASURE_INTERVAL_MS)
        return () => window.clearInterval(interval)
    }, [elements])

    return (
        <>
            <TourCanvas elements={canvasElements} />
            <TourTexts elements={canvasElements} />
        </>
    )
}

export default memo(TourStep)
